<script lang="ts">
    import Component from 'vue-class-component';
    import Vue from "vue";
    import { PanelistVoteReadModel } from '@Evaluation/Estimations/Domain/readmodels';
    import { isNullOrWhitespace, numCompare } from '@common/Common';

    @Component({
        props: {
            AlternativeName: String,
            Entries: Array,
            HasScale: Boolean,
            ScaleLevels: Array
        },
    })
    export default class VotesBrowser extends Vue
    {
        ActiveTab: string = "votes";

        get QuestionScale(): number[] { return this.$props.ScaleLevels.map(a => a.Value); }
        get VotesEntries(): PanelistVoteReadModel[] { return this.$props.Entries; }
        get Comments(): PanelistVoteReadModel[] { return this.VotesEntries.filter(a => isNullOrWhitespace(a.Comment) === false); }

        get SummaryTabActive(): boolean { return this.ActiveTab === "summary"; }
        get VotesTabActive(): boolean { return this.ActiveTab === "votes"; }
        get CommentsTabActive(): boolean { return this.ActiveTab === "notes"; }


        ShowSummaryTabCommand()
        {
            this.ActiveTab = "summary";
        }

        ShowVotesTabCommand()
        {
            this.ActiveTab = "votes";
        }

        ShowCommentsTabCommand()
        {
            this.ActiveTab = "notes";
        }

        
        get Average(): number
        {
            const votesEntries = this.VotesEntries.filter(a => a.Value != null);

            if (votesEntries.length === 0)
            {
                return null;
            }

            let avg = 0.0;

            for (let voteEntry of votesEntries)
            {
                avg += voteEntry.Value;
            }

            const sumOfWeights = votesEntries.length;
            avg = avg / sumOfWeights;
            //return Math.Round(avg, 2);

            return avg;
        }

        get AverageCells(): TdViewModel[]
        {
            const cells = this.QuestionScale.map(val => new TdViewModel(val, false, false));

            if (this.Average != null)
            {
                const scaleValue = this.QuestionScale.find(a => Math.round(100 * a) === 100 * this.Average);

                if (scaleValue != null)
                {
                    cells.find(a => a.Value === scaleValue).Highlighted = true;
                }
                else
                {
                    const before = this.QuestionScale.filter(a => a < this.Average).length;

                    const cell = cells[before - 1];
                    cell.Highlighted = true;
                    cell.Value = this.Average;
                    cell.Colspan = 2;

                    cells.remove(cells[before]);
                }
            }

            return cells;
        }

        get HistogramValues(): any[]
        {
            const result: { Value: number, Count: number, PercentOfMax: number }[] = [];

            const votesValues = this.VotesEntries.filter(a => a.Value != null).map(a => a.Value);

            let maxCount = 0;

            for (let value of this.QuestionScale)
            {
                const count = votesValues.filter(a => a === value).length;
                maxCount = Math.max(maxCount, count);
            }

            for (let value of this.QuestionScale)
            {
                const count = votesValues.filter(a => a === value).length;
                const percentOfMax = Math.round(count / maxCount * 100);

                result.push({ Value: value, Count: count, PercentOfMax: percentOfMax });
            }

            return result;
        }
    }

    class TdViewModel
    {
        Colspan: number;
        Highlighted: boolean;
        Value: number;

        constructor(value: number, highlighted: boolean, colspan: boolean)
        {
            this.Colspan = colspan ? 2 : 1;
            this.Highlighted = highlighted;
            this.Value = value;
        }
    }
</script>

<template>

    <div>
        <div v-if="HasScale" class="DirectRateVotesBrowserView">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" @click="ShowVotesTabCommand" :class="{ 'active': VotesTabActive }">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ShowSummaryTabCommand" :class="{ 'active': SummaryTabActive }">Chart</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ShowCommentsTabCommand" :class="{ 'active': CommentsTabActive }">Comments</a>
                </li>
            </ul>

            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{ 'active': VotesTabActive }" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>
                                            Average ({{Average != null ? Average.toFixedIfNeeded(2) : 'N/A'}})
                                        </strong>
                                    </td>
                                    <td v-for="c in AverageCells" :class="{ 'bg-color-gray': c.Highlighted, 'td-1': c.Colspan === 1, 'td-2': c.Colspan > 1 }" :colspan="c.Colspan">
                                        <span :class="{ 'fg-color-white': c.Highlighted }">{{c.Value.toFixedIfNeeded(2)}}</span>
                                    </td>
                                </tr>

                                <tr v-for="e in Entries" :key="e.ProjectUserId" style="text-align: center;">
                                    <td style="text-align: left;">
                                        {{e.ProjectUserName ?? '???'}}
                                    </td>
                                    <td v-for="lvl in QuestionScale" :class="{ 'bg-color-green': lvl === e.Value }">
                                        <span :class="{ 'fg-color-white': lvl === e.Value }">
                                            {{lvl}}
                                        </span>
                                    </td>
                                </tr>

                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="SummaryTabActive" :class="{ 'active': SummaryTabActive }" class="tab-pane pt-2">
                    <div class="table-responsive">
                        <table class="table">
                            <tbody>
                                <tr style="text-align: center;" class="bottom noborder">
                                    <td v-for="hv in HistogramValues" style="height: 400px; border: none; padding-top: 20px;">
                                        <div v-if="hv.Count">{{hv.Count}}</div>
                                        <div class="bg-color-green2" :style="{height: hv.PercentOfMax + '%'}"></div>
                                    </td>
                                </tr>
                                <tr style="text-align: center;">
                                    <td v-for="v in QuestionScale" style="width: 5%;">{{v.toFixedIfNeeded(2)}}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote v-for="c in Comments" class="blockquote">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.ProjectUserName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>

        <div v-if="!HasScale" class="DirectValueVotesBrowserView">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" :class="{ 'active': VotesTabActive }" @click="ShowVotesTabCommand">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" :class="{ 'active': CommentsTabActive }" @click="ShowCommentsTabCommand">Comments</a>
                </li>
            </ul>
            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{ 'active': VotesTabActive }" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>
                                            <span>Average</span>

                                        </strong>
                                    </td>
                                    <td>
                                        <span>{{Average != null ? Average.toFixedIfNeeded(2) : 'N/A'}}</span>
                                    </td>
                                </tr>

                                <tr v-for="e in VotesEntries" style="text-align: center;">
                                    <td style="text-align: left;">
                                        {{e.ProjectUserName ?? '???'}}
                                    </td>
                                    <td>
                                        {{e.Value}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote class="blockquote" v-for="c in Comments">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.ProjectUserName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>
    </div>



</template>

<style scoped>
    .DirectRateVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }

    .DirectRateVotesBrowserView td.td-1 {
        width: 5%;
    }

    .DirectRateVotesBrowserView td.td-2 {
        width: 10%;
    }

</style>